import React from 'react';
import { Tab } from 'ui';
import { displayPrice, pluralize, thousandsSeparator } from 'utils';
import Image from '~/components/Image';
import { usePageViewData } from '~/contexts/PageViewDataContext';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';
import { RailsModelPriceStatistics } from '~/typings/services/rails/item';

function getPercentilesFromModelPriceStatistics(
  priceStatistics?: RailsModelPriceStatistics,
  conditionSlug?: string,
) {
  if (priceStatistics) {
    if (conditionSlug != null) {
      if (conditionSlug === 'new') {
        return [+priceStatistics.new_p30!, +priceStatistics.new_p70!] as const;
      }

      return [+priceStatistics.used_p30!, +priceStatistics.used_p70!] as const;
    }
    return [
      +priceStatistics.used_p30! || +priceStatistics.new_p30!,
      +priceStatistics.new_p70! || +priceStatistics.used_p70!,
    ] as const;
  }
}

interface Props {}

const ModelLanderHeader: React.FC<Props> = () => {
  const { filterState, facets, lander, paging, changeFacetSelection } =
    useFacetFiltering();
  const { pageView } = usePageViewData();

  if (!lander) {
    return null;
  }

  if (!lander.model) {
    return null;
  }

  const priceStatistics = getPercentilesFromModelPriceStatistics(
    lander.model.price_statistics,
  );

  const itemTypeFacet = facets
    .find(f => f.slug === 'item_type')
    ?.children?.find(child => child.slug === 'recently_sold');

  const isRecentlySoldOn = !!(itemTypeFacet
    ? filterState.item_type?.includes(itemTypeFacet.id)
    : false);

  return (
    <div className="mb-4 rounded-sm bg-white px-4 pt-4 shadow">
      <div className="flex space-x-2 md:space-x-8">
        <Image
          src={lander.model.primary_image?.small_url}
          className="h-16 w-16 rounded object-cover md:h-24 md:w-24"
        />

        <div className="space-y-2">
          <h1>{lander.name}</h1>

          {lander.model.position && lander.model.position > 0 && (
            <div>
              <b>#{lander.model.position}</b>
              {lander.model.category_name && (
                <span> in {lander.model.category_name}</span>
              )}
            </div>
          )}

          {priceStatistics && (
            <div>
              From:{' '}
              <b>
                {displayPrice(priceStatistics[0])}
                {' - '}
                {displayPrice(priceStatistics[1])}
              </b>
            </div>
          )}

          {lander.model.price_retail && (
            <div>
              Retail: <b>{displayPrice(lander.model.price_retail)}</b>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex space-x-2">
        <Tab
          selected={!isRecentlySoldOn}
          onClick={() => changeFacetSelection('item_type', null)}
        >
          {thousandsSeparator(paging?.total_count)}{' '}
          {pluralize(paging?.total_count, 'Result', 'Results')}
        </Tab>

        {pageView?.blocks?.find(b => b.layout === 'buying-guide')?.tiles
          ?.length! > 0 ? (
          <Tab
            onClick={() =>
              document
                .querySelector('#buying-guide')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            Product Description
          </Tab>
        ) : null}

        {pageView?.blocks?.find(b => b.title === 'Similar Products') ? (
          <Tab
            onClick={() =>
              document
                .querySelector('#popular-models')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            Similar Products
          </Tab>
        ) : null}

        {!!itemTypeFacet ? (
          <Tab
            selected={isRecentlySoldOn}
            onClick={() => changeFacetSelection('item_type', itemTypeFacet)}
          >
            Recently Sold
          </Tab>
        ) : null}
      </div>
    </div>
  );
};

const ExpertPickIcon = ({ height = 32, width = 23, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height}>
    <defs>
      <path id="expert-pick-path" d="M0 .04h22.51v22.74H0z" />
    </defs>
    <g fill="none" fillRule="evenodd">
      <mask id="expert-pick-mask" fill="#fff">
        <use xlinkHref="#expert-pick-path" />
      </mask>
      <path
        fill="#FCB300"
        d="M11.27 2.38a8.97 8.97 0 0 0-8.93 8.98 8.97 8.97 0 0 0 8.82 9.07 8.97 8.97 0 0 0 9.01-8.97 8.97 8.97 0 0 0-8.9-9.08m0 20.4C5.06 22.78 0 17.66 0 11.38A11.3 11.3 0 0 1 11.25.04a11.3 11.3 0 0 1 11.26 11.39c0 6.25-5.06 11.35-11.24 11.35"
        mask="url(#expert-pick-mask)"
      />
      <path
        fill="#FCB300"
        d="M3.58 21.04a12.55 12.55 0 0 0 6.2 2.69c-.03.1-.05.21-.09.3l-2.67 7.49c-.1.25-.2.48-.52.48-.3 0-.44-.2-.54-.46L4.6 28.08l-2.7 1.58c-.2.11-.39.27-.6.34-.17.05-.42.07-.53-.02-.13-.1-.2-.35-.18-.53.03-.27.16-.52.25-.79l2.62-7.33.11-.3m8.86 2.79c2.33-.29 4.4-1.1 6.3-2.49.05.14.1.24.13.35l2.7 7.62c.1.26.1.48-.12.67-.23.18-.45.13-.68 0l-2.9-1.64-.32-.18-1.29 3.3-.07.18c-.09.25-.26.38-.53.37-.27 0-.4-.18-.47-.4l-1.68-4.75-.92-2.61c-.05-.13-.1-.26-.14-.42"
      />
      <path
        fill="#FEFEFE"
        d="M11.24 19.5a7.97 7.97 0 0 0 7.94-7.94 8 8 0 0 0-7.87-8.16 8 8 0 0 0-7.97 8 7.98 7.98 0 0 0 7.9 8.1m.04-17.13a8.96 8.96 0 0 1 8.87 9.1 8.96 8.96 0 0 1-8.99 8.97c-4.84 0-8.8-4.1-8.79-9.07.01-4.98 4-9 8.9-9"
      />
      <path
        fill="#FCB300"
        d="M13.44 8.7V7.28c0-1.35-.9-2.36-2.23-2.52-.47-.06-.68.12-.69.6v1.92c0 .11-.01.25-.08.33-.53.62-1.03 1.31-2 1.29-.23-.34-.6-.49-1.05-.46h-.64c-.78 0-1.29.47-1.3 1.23a381.74 381.74 0 0 0 0 5.86c0 .57.34 1.07.84 1.15.78.13 1.6.32 2.19-.42.3.08.57.17.85.21.29.04.58.04.86.04H14c1.07 0 1.67-.78 1.4-1.8-.02-.09-.02-.21.02-.26.5-.49.5-1.05.27-1.65.58-.5.6-1.1.26-1.73.7-.59.6-1.48.14-1.95-.3-.31-.66-.44-1.1-.44h-1.55m-2.2 10.87a7.99 7.99 0 1 1 .07-16 8 8 0 0 1-.07 16"
      />
      <path
        fill="#FEFEFE"
        d="M7.45 15.9V9.5h-.71c-.38 0-.42.03-.42.43v5.6c0 .28.1.39.38.37h.75m3.98-10.01l-.02.35c0 .43-.02.86 0 1.29a.99.99 0 0 1-.3.8c-.3.3-.59.6-.85.93-.26.33-.56.53-1 .51-.13 0-.26.06-.4.1-.25.08-.37.23-.37.54.02 1.44.01 2.88.01 4.32 0 .66.03.7.67.87l.21.03 4.59.02c.21 0 .35-.11.36-.33.01-.21-.1-.34-.31-.38h-.1c-.28-.06-.44-.24-.42-.51.01-.28.18-.45.47-.47.1-.01.19 0 .28 0 .25 0 .42-.11.43-.38.01-.25-.13-.38-.39-.4-.3-.02-.45-.2-.44-.51.02-.29.18-.44.47-.46h.2c.23 0 .43-.1.45-.35.02-.24-.15-.34-.38-.37-.32-.05-.46-.24-.44-.53.01-.29.18-.43.5-.46h.2c.26 0 .47-.06.47-.38s-.22-.38-.48-.38h-1.78c-.58 0-.69-.11-.7-.7 0-.66.02-1.33-.03-2-.04-.54-.37-.9-.9-1.15m1.86 2.81h1.55c.43 0 .79.13 1.09.44a1.34 1.34 0 0 1-.14 1.98c.33.63.32 1.22-.26 1.73.23.61.22 1.18-.26 1.67-.05.05-.04.17-.02.25.26 1.04-.34 1.83-1.4 1.83h-3.79c-.28 0-.57 0-.85-.04-.29-.05-.56-.14-.86-.22-.58.75-1.4.55-2.17.43-.5-.09-.83-.6-.84-1.17V9.7c.01-.77.52-1.23 1.3-1.24h.63c.45-.03.81.12 1.04.46.97.03 1.47-.67 2-1.3.06-.08.07-.22.07-.33V5.35c.01-.48.22-.66.69-.6a2.46 2.46 0 0 1 2.22 2.54V8.7"
      />
      <path
        fill="#FCB300"
        d="M11.52 5.63c.53.24.86.61.9 1.16.05.66.03 1.33.04 2 0 .58.1.7.69.7h1.78c.26 0 .47.06.48.38 0 .32-.21.38-.47.38h-.2c-.32.02-.49.17-.5.46-.02.3.12.48.43.53.24.03.4.13.4.37-.03.26-.23.35-.47.35h-.19c-.3.02-.45.18-.47.46-.01.3.13.5.44.52.26.02.4.14.4.4-.02.26-.2.37-.44.37h-.28c-.29.02-.46.2-.47.47-.01.28.14.46.42.51h.1c.2.05.32.18.31.4-.01.2-.15.32-.36.32a2162.92 2162.92 0 0 1-4.58-.02.86.86 0 0 1-.22-.03c-.63-.18-.66-.21-.66-.87v-4.33c-.01-.3.1-.46.37-.54.13-.04.26-.1.4-.1.43.02.73-.18.99-.52.26-.32.54-.63.84-.93a.99.99 0 0 0 .3-.8v-1.3l.02-.34M7.7 15.7h-.78c-.29.02-.4-.1-.4-.38V9.62c0-.4.04-.43.43-.43h.75v6.5z"
      />
    </g>
  </svg>
);

export default ModelLanderHeader;
